import { useMemo } from 'react'
// Material
import { Divider } from '@mui/material'
import { decodeSubscriptionPermission } from '@weblite/permission'
import { Admin } from '@weblite/ts-action/subscription'
import { AddUserIcon } from '$common/icons'
import { MenuIcon } from '$common/icons/menu'
import { UserListItemSkeleton, UserListItem } from '$common/user-list-item'
import { EmptyStateList } from '$common/empty-state-list'
import { withSearchableListPage } from '$common/searchable-list-page'
import {
  useClientUserPermissions,
  useHallManagers,
  useSearchedHallAdmins,
} from '$logic/subscription/react-hook'
import { showHallMemberMenu } from '$comp/modals/menus/hall-member-menu'
import {
  closeHallAdmins,
  openAddHallAdmin,
  openUserProfile,
} from '$logic/router'
import { getUserDisplayName, useUser } from '$logic/users'
import { t } from '$i18n'
import { useClientUser } from '$logic/client-user'
import { ChannelManager } from '$logic/subscription/question-support.action'

function ListItem({
  id,
  roomId,
  isClientUserOwner,
  promoterId,
  decodedPermissions,
  role,
}: Admin & {
  role?: ChannelManager['role']
  roomId: string
  isClientUserOwner: boolean
  decodedPermissions: ReturnType<typeof decodeSubscriptionPermission>
}) {
  const { user: promoter } = promoterId ? useUser(promoterId) : { user: null }
  const clientUserId = useClientUser()?.id
  const promotedByClientUser = !isClientUserOwner && promoterId === clientUserId
  return (
    <>
      <UserListItem
        userId={id}
        hasDivider={false}
        onClick={() => openUserProfile(id)}
        role={
          decodedPermissions?.isOwner
            ? t('common.owner')
            : role === 'QUESTION_SUPPORT'
              ? t('admins.questionSupport')
              : null
        }
        secondaryText={
          promoter
            ? `${t('admins.promotedBy')} ${getUserDisplayName(promoter)}`
            : ''
        }
        SecondaryActionIcon={
          (isClientUserOwner && !decodedPermissions?.isOwner) ||
          promotedByClientUser
            ? MenuIcon
            : undefined
        }
        onSecondaryActionIconClick={() =>
          showHallMemberMenu({
            hallId: roomId,
            userId: id,
            isAdmin: true,
            isQuestionSupport: role === 'QUESTION_SUPPORT',
            isInAdminsPage: true,
            promotedByClientUser,
          })
        }
      />
      <Divider variant="inset" />
    </>
  )
}

function EmptyStateComponent() {
  return <EmptyStateList />
}

export function Admins({ roomId }: { roomId: string }) {
  const clientUserPermissions = useClientUserPermissions(roomId)

  const Page = useMemo(
    () =>
      withSearchableListPage({
        appBarTitle: t('admins.title'),
        ListItem: item => (
          <ListItem
            {...item}
            roomId={roomId}
            isClientUserOwner={clientUserPermissions?.isOwner}
          />
        ),
        SkeletonItem: UserListItemSkeleton,
        EmptyStateComponent,
        fetchHook: () => useHallManagers(roomId, 'admins'),
        searchHook: useSearchedHallAdmins,
        actionButtons: [
          {
            text: t('admins.addAdmin'),
            icon: <AddUserIcon color="secondary" />,
            onClick: () => openAddHallAdmin(roomId),
          },
        ],
        onBack: closeHallAdmins,
      }),
    [roomId, clientUserPermissions?.isOwner],
  )

  return (
    <Page
      fetchHookParams={roomId}
      getSearchHookParams={(query, items) => ({
        query,
        fetchedItems: items,
      })}
      hideActionButton={!clientUserPermissions?.addNewAdmins}
    />
  )
}
